import { ChargeEvent, Coordinate } from "@fairfleet/geotab";
import { round } from "lodash";
import { EnergyDevice } from "./energyDevice";
import { TripContext } from "./tripContext";
import { durationToMillis } from "@/components/grid/cells/durationToMillis";
import { reportedNumber } from "@/utilities/reportedNumber";
import { toCoordinate } from "@/utilities/toCoordinate";

/** Consecutive charge events closer together than this are treated as one plug-in session. */
const MERGE_GAP_MS = 60 * 60 * 1000;

export interface ChargeEventRow {
  id: string;
  deviceId: string;
  name: string;
  licensePlate: string;
  startTime: Date;
  durationHours: number | null;
  startStateOfCharge: number | null;
  endStateOfCharge: number | null;
  energyConsumedKwh: number | null;
  /** Geotab itself says this energy figure was estimated from secondary signals, not measured. */
  energyIsEstimated: boolean;
  distanceSinceLastChargeKm: number | null;
  /**
   * Whether the distance was worked out from the odometer readings of two charge events rather
   * than reported by Geotab, whose own field many vehicles never populate.
   */
  distanceIsDerived: boolean;
  odometerKm: number | null;
  /** The driver as an id: the trip references one without naming it. */
  driverId: string | null;
  location: Coordinate | null;
}

/**
 * Turns the charge events of one vehicle into rows, oldest first.
 *
 * @param device - The vehicle the events belong to.
 * @param events - The charge events as Geotab returned them.
 * @param tripAt - Resolves what the vehicle was doing at a given moment.
 * @returns The rows, oldest first.
 */
export function toChargeEventRows(
  device: EnergyDevice,
  events: ChargeEvent[],
  tripAt: (time: number) => TripContext,
): ChargeEventRow[] {
  const sorted = [...events].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  );

  let previousOdometerKm: number | null = null;

  return sorted.map((event, index) => {
    const startTime = new Date(event.startTime);
    const odometerKm = reportedNumber(event.chargingStartedOdometerKm);
    const reportedDistance = reportedNumber(event.electricDistanceSinceLastChargeKm);

    // The distance-since-last-charge Geotab reports is often left empty by the vehicle. The gap
    // between this and the previous charge odometer reading answers the same question, so use it
    // rather than showing nothing — but say which of the two the number came from.
    const derivedDistance =
      odometerKm != null && previousOdometerKm != null
        ? Math.max(0, odometerKm - previousOdometerKm)
        : null;
    if (odometerKm != null) {
      previousOdometerKm = odometerKm;
    }

    const durationMs = durationToMillis(event.duration);

    return {
      // ChargeEvent carries an id, but a merged session needs a stable id of its own and the
      // start time is what identifies an event on a vehicle either way.
      id: `${device.id}-${startTime.getTime()}-${index}`,
      deviceId: device.id,
      name: device.name,
      licensePlate: device.licensePlate,
      startTime,
      durationHours: durationMs != null ? decimalHours(durationMs) : null,
      startStateOfCharge: reportedNumber(event.startStateOfCharge),
      endStateOfCharge: reportedNumber(event.endStateOfCharge),
      energyConsumedKwh: reportedNumber(event.energyConsumedKwh),
      energyIsEstimated: !!event.chargeIsEstimated,
      distanceSinceLastChargeKm: reportedDistance ?? derivedDistance,
      distanceIsDerived: reportedDistance == null && derivedDistance != null,
      odometerKm,
      driverId: tripAt(startTime.getTime()).driverId,
      location: toCoordinate(event.location),
    };
  });
}

/**
 * Groups consecutive charge events of the same plug-in session into one row, the way the report
 * built into Geotab presents them.
 *
 * The API exposes no "still connected" flag, only a start time and a duration per event, so
 * whether two events belong to one plug-in has to be inferred from the gap between them. A long
 * idle stretch while still plugged in — topped up, then trickle-charging hours later — therefore
 * may not group exactly as the Geotab report does. Hence the toggle in the UI: the ungrouped list is
 * always one click away.
 *
 * @param rows - The rows to group, in any order.
 * @returns The grouped rows.
 */
export function mergeChargeSessions(rows: ChargeEventRow[]): ChargeEventRow[] {
  const byDevice = new Map<string, ChargeEventRow[]>();
  for (const row of rows) {
    const existing = byDevice.get(row.deviceId);
    if (existing) existing.push(row);
    else byDevice.set(row.deviceId, [row]);
  }

  const merged: ChargeEventRow[] = [];

  for (const deviceRows of byDevice.values()) {
    const sorted = [...deviceRows].sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    let session: ChargeEventRow | null = null;
    let sessionEndMs = 0;

    for (const row of sorted) {
      const rowEndMs = row.startTime.getTime() + fromDecimalHours(row.durationHours);

      if (session && row.startTime.getTime() - sessionEndMs <= MERGE_GAP_MS) {
        session.energyConsumedKwh = sum(session.energyConsumedKwh, row.energyConsumedKwh);
        session.endStateOfCharge = row.endStateOfCharge ?? session.endStateOfCharge;
        session.energyIsEstimated = session.energyIsEstimated || row.energyIsEstimated;
        // Distance, odometer, driver and location stay with the event the session started with:
        // they describe the state before the vehicle was plugged in.
        sessionEndMs = Math.max(sessionEndMs, rowEndMs);
        session.durationHours = decimalHours(sessionEndMs - session.startTime.getTime());
        continue;
      }

      if (session) merged.push(session);
      session = { ...row, id: `${row.id}-session` };
      sessionEndMs = rowEndMs;
    }

    if (session) merged.push(session);
  }

  return merged;
}

function sum(first: number | null, second: number | null) {
  if (first == null && second == null) return null;
  return (first ?? 0) + (second ?? 0);
}

/** Milliseconds as decimal hours, which is what the grid column and the export both want. */
function decimalHours(milliseconds: number) {
  return round(milliseconds / 3_600_000, 2);
}

function fromDecimalHours(hours: number | null) {
  return (hours ?? 0) * 3_600_000;
}

